import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE, apiFetch } from '../lib/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from 'react-toastify';

type FaqItem = {
    id: number;
    question: string;
    answer: string;
    paraphrases: string[];
    created_at?: string;
    updated_at?: string;
};

const FaqManager: React.FC = () => {
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [paraphrases, setParaphrases] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [filter, setFilter] = useState('');
    const [pendingDelete, setPendingDelete] = useState<FaqItem | null>(null);

    const showMessage = (text: string, isError = false) => {
        if (isError) {
            toast.error(text);
        } else {
            toast.success(text);
        }
    };

    const loadFaqs = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/faqs`);
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to load FAQs', true);
                return;
            }
            const rows: FaqItem[] = (data?.faqs ?? []).map((row: any) => ({
                id: Number(row.id),
                question: String(row.question ?? ''),
                answer: String(row.answer ?? ''),
                paraphrases: Array.isArray(row.paraphrases) ? row.paraphrases.map((x: any) => String(x)) : [],
                created_at: row.created_at,
                updated_at: row.updated_at,
            }));
            setFaqs(rows);
        } catch {
            showMessage('Failed to load FAQs', true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFaqs();
    }, []);

    const resetForm = () => {
        setQuestion('');
        setAnswer('');
        setParaphrases('');
        setEditingId(null);
    };

    const handleSave = async () => {
        const trimmedQuestion = question.trim();
        const trimmedAnswer = answer.trim();
        if (!trimmedQuestion || !trimmedAnswer) {
            showMessage('Question and answer are required', true);
            return;
        }

        const parsedParaphrases = paraphrases
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        setSaving(true);
        try {
            const res = await apiFetch(`${API_BASE}/api/faqs${editingId ? `/${editingId}` : ''}`, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    question: trimmedQuestion,
                    answer: trimmedAnswer,
                    paraphrases: parsedParaphrases,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to save FAQ', true);
                return;
            }
            showMessage(editingId ? 'FAQ updated' : 'FAQ added');
            resetForm();
            await loadFaqs();
        } catch {
            showMessage('Failed to save FAQ', true);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (faq: FaqItem) => {
        setEditingId(faq.id);
        setQuestion(faq.question);
        setAnswer(faq.answer);
        setParaphrases(faq.paraphrases.join('\n'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const confirmDelete = async () => {
        const faq = pendingDelete;
        if (!faq) return;
        setDeletingId(faq.id);
        try {
            const res = await apiFetch(`${API_BASE}/api/faqs/${faq.id}`, {
                method: 'DELETE',
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                showMessage(data?.message ?? 'Failed to remove FAQ', true);
                return;
            }
            showMessage('FAQ removed');
            setFaqs((prev) => prev.filter((item) => item.id !== faq.id));
        } catch {
            showMessage('Failed to remove FAQ', true);
        } finally {
            setDeletingId(null);
            setPendingDelete(null);
        }
    };

    const filteredFaqs = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return faqs;
        return faqs.filter((faq) =>
            faq.question.toLowerCase().includes(q) ||
            faq.answer.toLowerCase().includes(q) ||
            faq.paraphrases.some((p) => p.toLowerCase().includes(q))
        );
    }, [faqs, filter]);

    return (
        <div className="p-4 h-full flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">FAQ Manager</h1>
                <button
                    onClick={loadFaqs}
                    className="px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                    disabled={loading}
                >
                    Refresh
                </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)] flex-1 min-h-0">
                <div className="bg-white rounded-xl shadow p-4 border border-slate-100 h-full overflow-y-auto">
                    <h2 className="text-lg font-semibold mb-3">{editingId ? 'Edit FAQ' : 'Add FAQ'}</h2>
                    <div className="grid gap-4">
                        <label className="text-sm text-slate-700">
                            Question
                            <input
                                className="mt-1 w-full border border-slate-200 rounded-lg p-2 outline-none focus:ring-2 focus:ring-sky-200"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder="e.g., What is the total outstanding in my account?"
                            />
                        </label>
                        <label className="text-sm text-slate-700">
                            Answer Template
                            <textarea
                                className="mt-1 w-full min-h-[120px] border border-slate-200 rounded-lg p-2 outline-none focus:ring-2 focus:ring-sky-200"
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                placeholder="Use placeholders like [amount], [date], [invoice_no]"
                            />
                        </label>
                        <label className="text-sm text-slate-700">
                            Paraphrases (one per line)
                            <textarea
                                className="mt-1 w-full min-h-[80px] border border-slate-200 rounded-lg p-2 outline-none focus:ring-2 focus:ring-sky-200"
                                value={paraphrases}
                                onChange={(e) => setParaphrases(e.target.value)}
                                placeholder="e.g., How much do I owe?\nWhat is my total outstanding?"
                            />
                        </label>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300"
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : editingId ? 'Update FAQ' : 'Add FAQ'}
                        </button>
                        {editingId && (
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                            >
                                Cancel Edit
                            </button>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow p-4 border border-slate-100 h-full flex flex-col overflow-hidden">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sticky top-0 bg-white pb-3 z-10">
                        <h2 className="text-lg font-semibold">Saved FAQs</h2>
                        <input
                            className="w-full sm:w-72 border border-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-sky-200"
                            placeholder="Search questions or answers..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                        {loading ? (
                            <div className="py-6 text-sm text-slate-500">Loading FAQs...</div>
                        ) : filteredFaqs.length === 0 ? (
                            <div className="py-6 text-sm text-slate-500">No FAQs found.</div>
                        ) : (
                            <div className="mt-1 space-y-3">
                                {filteredFaqs.map((faq) => (
                                    <div key={faq.id} className="border border-slate-200 rounded-lg p-4">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-slate-900">{faq.question}</div>
                                                <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{faq.answer}</div>
                                                {faq.paraphrases.length > 0 && (
                                                    <div className="text-xs text-slate-500 mt-2">
                                                        Paraphrases: {faq.paraphrases.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2 shrink-0">
                                                <button
                                                    onClick={() => handleEdit(faq)}
                                                    className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => setPendingDelete(faq)}
                                                    className="px-3 py-1.5 text-sm rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50"
                                                    disabled={deletingId === faq.id}
                                                >
                                                    {deletingId === faq.id ? 'Removing...' : 'Remove'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmDialog
                open={!!pendingDelete}
                title="Remove FAQ"
                message={<>Remove FAQ <span className="font-medium text-gray-800">“{pendingDelete?.question}”</span>?</>}
                confirmLabel="Remove"
                danger
                busy={deletingId !== null}
                onConfirm={confirmDelete}
                onCancel={() => { if (deletingId === null) setPendingDelete(null); }}
            />
        </div>
    );
};

export default FaqManager;
